// src/features/gatherings/components/modals/RejectPendingEditModal.tsx
import React, { useState } from 'react';
import {
  Modal,
  Box,
  IconButton,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Gathering } from '../../../../types';
import { rejectGatheringPendingEdit } from '../../../../services/api/endpoints/gatherings';

interface RejectPendingEditModalProps {
  gatheringId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Receives the gathering as returned by the reject call (content unchanged). */
  onSuccess?: (gathering: Gathering) => void;
  token: string;
  gatheringTitle?: string;
}

// Style for the modal content using MUI patterns
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '600px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  borderRadius: 2,
  outline: 0
};

/**
 * Confirms rejecting a host's staged edit (ENG-1097).
 *
 * Notes are optional server-side, so this does not block on an empty box — but
 * the reject is destructive (the staged changes are discarded), so it is always
 * an explicit confirmation step rather than a one-click action.
 */
const RejectPendingEditModal: React.FC<RejectPendingEditModalProps> = ({
  gatheringId,
  isOpen,
  onClose,
  onSuccess,
  token,
  gatheringTitle
}) => {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const updatedGathering = await rejectGatheringPendingEdit(
        token,
        gatheringId,
        notes
      );

      setNotes('');
      if (onSuccess) {
        onSuccess(updatedGathering);
      }
      onClose();
    } catch (err) {
      // The staged edit can disappear between page load and click (withdrawn
      // by the host, or expired/superseded once ENG-1096 lands), so show what
      // the API said rather than a generic failure.
      console.error('Error rejecting pending edit:', err);
      setError(
        err instanceof Error && err.message
          ? `Could not reject these changes: ${err.message}`
          : 'Could not reject these changes. Please refresh and try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNotes('');
    setError(null);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={isSubmitting ? undefined : handleCancel}
      aria-labelledby="reject-pending-edit-modal-title"
    >
      <Box sx={modalStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography id="reject-pending-edit-modal-title" variant="h6" component="h2">
            Reject Pending Changes
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            disabled={isSubmitting}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {gatheringTitle && (
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            <strong>Gathering:</strong> {gatheringTitle}
          </Typography>
        )}

        <Typography variant="body1" sx={{ mb: 2 }}>
          The host's proposed changes will be discarded. The live gathering stays
          exactly as it is now, and guests are unaffected.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Reviewer notes (optional)"
          multiline
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Why are these changes being rejected?"
          fullWidth
          disabled={isSubmitting}
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
            color="error"
          >
            {isSubmitting ? 'Rejecting...' : 'Reject Changes'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RejectPendingEditModal;
